import { Menubar, MenubarContent, MenubarItem, MenubarMenu, MenubarTrigger } from '@luminara/ui-components';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Logo from '../../../../../assets/Logo.svg';
import ContactIcon from '../../../../../assets/navbar/Message_light.svg';
import RequestIcon from '../../../../../assets/navbar/NewRequestIcon.svg';
import MySelections from '@assets/navbar/MySelections.svg?react';
import ViewIcon from '../../../../../assets/navbar/ViewIcon.svg';
import SoundOff from '../../../../../assets/navbar/music-off.svg';
import SoundOn from '../../../../../assets/navbar/music-on.svg';
import { DEFAULT_SOUNDTRACK_VOLUME_LEVEL } from '../../../../../constants/app-constants';
import { navigateToClientSettingsAction } from '../../../../../core/usecases/client-user-profile/navigateToClientSettingsAction';
import { setSoundtrackVolumeFromVolumeSlider } from '../../../../../core/usecases/music/SetSoundtrackVolumeFromVolumeSlider';
import { soundIconClicked } from '../../../../../core/usecases/music/SoundIconClicked';
import { navigateToClientBookedAppointmentsScreenAction } from '../../../../../core/usecases/shared/navigateToClientBookedAppointmentsScreenAction';
import { navigateToClientDashboardAction } from '../../../../../core/usecases/shared/navigateToClientDashboardAction';
import { navigateToMyHealersScreenAction } from '../../../../../core/usecases/shared/navigateToMyHealersScreenAction';
import { navigateToRequestNewHealerAction } from '../../../../../core/usecases/shared/navigateToRequestNewHealerAction';
import { NavigationOption } from '../../../../../store/reducers/global.reducer';
import {
  selectAvailableNavigationOptionsForClient,
  selectClientActiveTab,
  selectCurrentUserId,
  selectIsMusicEnabled,
  selectIsVolumeSliderVisible,
  selectShowSoundIcon,
  selectSoundtrack,
  selectSoundtrackVolume,
} from '../../../../../store/selectors/global.selector';
import { selectSessionStatus } from '../../../../../store/selectors/meeting.selector';
import VolumeSlider from '../VolumeSlider';
import MusicPlayer from '../background-music-player';
import ArrowDownIcon from './ArrowDownIcon';
import ClientBookedAppointmentsIcon from './ClientBookedAppointmentsIcon';
import ClientHomeIcon from './ClientHomeIcon';
import ClientMyHealersIcon from './ClientMyHealersIcon';
import ClientSettingsIcon from './ClientSettingsIcon';
import { navigateToMySelectionsAction } from '../../../../../core/usecases/shared/navigateToMySelectionsAction';
import styles from './ClientDesktopNavbar.module.css';

const ClientDesktopNavbar = () => {
  const clientUserProfileId = useSelector(selectCurrentUserId);
  const availableNavigationOptions: NavigationOption[] = useSelector(selectAvailableNavigationOptionsForClient);
  const showVolumeSlider = useSelector(selectIsVolumeSliderVisible);
  const musicOn = useSelector(selectIsMusicEnabled);
  const showSoundIcon = useSelector(selectShowSoundIcon);
  const soundtrackVolume: number = useSelector(selectSoundtrackVolume);
  const audioRef = useRef<HTMLAudioElement>(null);
  const activeTab: NavigationOption | undefined = useSelector(selectClientActiveTab);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const sessionStatus = useSelector(selectSessionStatus);

  const meeting = sessionStatus === "MEETING_IN_PROGRESS";
  const healloCompleted = sessionStatus === "HEALLO_COMPLETED";

  const accentColor = '#e37745';
  const blackColor = '#000';

  const soundtrack = useSelector(selectSoundtrack);
  const musicSelected = soundtrack != null;

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = soundtrackVolume;
    }
  }, [soundtrackVolume]);

  const handleHomeClicked = () => {
    if (clientUserProfileId) {
      dispatch<any>(navigateToClientDashboardAction(navigate, clientUserProfileId));
    }
  };

  const handleBookedAppointmentsClicked = () => {
    if (clientUserProfileId) {
      dispatch<any>(navigateToClientBookedAppointmentsScreenAction(navigate, clientUserProfileId));
    }
  };

  const handleMyHealersClicked = () => {
    if (clientUserProfileId) {
      dispatch<any>(navigateToMyHealersScreenAction(navigate, clientUserProfileId));
    }
  };

  const handleSettingsClicked = () => {
    if (clientUserProfileId) {
      dispatch<any>(navigateToClientSettingsAction(navigate, clientUserProfileId));
    }
  };

  const handleRequestNewHealerClicked = () => {
    if (clientUserProfileId) {
      dispatch<any>(navigateToRequestNewHealerAction(navigate, clientUserProfileId));
    }
  };

  const handleRequestMySelections = () => {
    if (clientUserProfileId) {
      dispatch<any>(navigateToMySelectionsAction(navigate, clientUserProfileId));
    }
  };

  const handleVolumeChange = (e: number[] | undefined) => {
    const volume = e?.at(0);
    dispatch<any>(setSoundtrackVolumeFromVolumeSlider(volume));
  };

  const handleSoundIconClicked = () => {
    dispatch<any>(soundIconClicked());
  };

  const handleContactUsClicked = () => {
    window.open('mailto:contact@luminara.care');
  };

  return (
    <div className={`w-[calc(100vw-32px)] h-[51px] mx-4 mt-4 fixed top-0 rounded-full ${meeting || healloCompleted ? 'bg-transparent' : 'bg-white'} px-14  flex justify-between items-center z-50`}>
      <Menubar className="w-screen  flex justify-between items-center bg-transparent border-none ">
        <div className="flex items-center" onClick={handleHomeClicked}>
          <img src={Logo} alt="Logo" className={`w-auto h-auto max-w-144 max-h-14 ${styles.menuItem}`} />
        </div>
        <div className="flex flex-center gap-8">
          {availableNavigationOptions.includes('HOME') && (
            <MenubarMenu>
              <MenubarTrigger className={`${activeTab === 'HOME' && 'group text-accent'} ${styles.menuItem}`} onClick={handleHomeClicked}>
                <p className=" group-focus:text-accent flex items-center gap-1 text-base font-normal ">
                  <ClientHomeIcon fillColor={activeTab === 'HOME' ? accentColor : blackColor} />
                  <span>Home</span>
                </p>
              </MenubarTrigger>
            </MenubarMenu>
          )}

          {availableNavigationOptions.includes('BOOKED_APPOINTMENTS') && (
            <MenubarMenu>
              <MenubarTrigger onClick={handleBookedAppointmentsClicked} className={`${activeTab === 'BOOKED_APPOINTMENTS' && 'group text-accent'} ${styles.menuItem}`}>
                <p className=" group-focus:text-accent flex items-center gap-1 text-base font-normal ">
                  <ClientBookedAppointmentsIcon fillColor={activeTab === 'BOOKED_APPOINTMENTS' ? accentColor : blackColor} />
                  Booked appointments
                </p>
              </MenubarTrigger>
            </MenubarMenu>
          )}

          {(availableNavigationOptions.includes('MY_HEALERS') || availableNavigationOptions.includes('REQUEST_NEW_HEALER')) && (
            <MenubarMenu>
              <MenubarTrigger className={`${activeTab === 'MY_HEALERS' && 'group text-accent'} ${styles.menuItem}`}>
                <p className="group-focus:text-accent flex items-center gap-1 text-base font-normal ">
                  <ClientMyHealersIcon fillColor={activeTab === 'MY_HEALERS' ? accentColor : blackColor} />
                  My healers
                  <ArrowDownIcon fillColor={activeTab === 'MY_HEALERS' ? accentColor : blackColor} />
                </p>
              </MenubarTrigger>
              <MenubarContent sideOffset={-8} className="bg-white">
                {availableNavigationOptions.includes('MY_HEALERS') && (
                  <MenubarItem onClick={handleMyHealersClicked} className={`${styles.menuItem}`}>
                    <p className="flex items-center gap-1 text-base font-normal ">
                      <img src={ViewIcon} alt="" />
                      View all
                    </p>
                  </MenubarItem>
                )}
                {availableNavigationOptions.includes('REQUEST_NEW_HEALER') && (
                  <MenubarItem onClick={handleRequestNewHealerClicked} className={`${styles.menuItem}`}>
                    <p className="flex items-center gap-1 text-base font-normal ">
                      <img src={RequestIcon} alt="" />
                      Request new healer
                    </p>
                  </MenubarItem>
                )}
                {availableNavigationOptions.includes('MY_SELECTIONS') && (
                  <MenubarItem onClick={handleRequestMySelections} className={`${styles.menuItem}`}>
                    <p className="flex items-center gap-1 text-base font-normal ">
                      <MySelections className={`w-[18px] h-[18px] `} />
                      My selections
                    </p>
                  </MenubarItem>
                )}
              </MenubarContent>
            </MenubarMenu>
          )}

          {availableNavigationOptions.includes('SETTINGS') && (
            <MenubarMenu>
              <MenubarTrigger onClick={handleSettingsClicked} className={`${activeTab === 'SETTINGS' && 'group text-accent'} ${styles.menuItem}`}>
                <p className=" group-focus:text-accent flex items-center gap-1 text-base font-normal ">
                  <ClientSettingsIcon fillColor={activeTab === 'SETTINGS' ? accentColor : blackColor} />
                  Settings
                </p>
              </MenubarTrigger>
            </MenubarMenu>
          )}

          {availableNavigationOptions.includes('CONTACT_US') && (
            <MenubarMenu>
              <MenubarTrigger onClick={handleContactUsClicked} className={`${styles.menuItem}`}>
                <p className="flex items-center gap-1 text-base font-normal ">
                  <img src={ContactIcon} alt="" />
                  Contact us
                </p>
              </MenubarTrigger>
            </MenubarMenu>
          )}
        </div>
        {musicSelected && showVolumeSlider && (
          <div className="flex justify-end w-[240px] gap-1">
            <div className="flex">
              { <img src={musicOn ? SoundOn : SoundOff} alt="" className="cursor-pointer w-5" onClick={handleSoundIconClicked} />}
            </div>
            <VolumeSlider
              value={musicOn ? [soundtrackVolume] : [0]}
              onValueChange={handleVolumeChange}
              defaultValue={musicOn ? [DEFAULT_SOUNDTRACK_VOLUME_LEVEL] : [0]}
            />
          </div>
        )}
      </Menubar>
      <MusicPlayer />
    </div>
  );
};

export default ClientDesktopNavbar;
